import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import axios from 'axios';
import TrainingCertificate from './TrainingCertificate';

// Portal-specific axios instance — reads employee_access_token, never access_token
const portalApi = axios.create({ baseURL: '/api' });
portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
portalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('employee_access_token');
      localStorage.removeItem('employee_refresh_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const Icons = {
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12,19 5,12 12,5"></polyline>
    </svg>
  ),
  Book: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  ),
  Clock: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle><polyline points="12,6 12,12 16,14"></polyline>
    </svg>
  ),
  Award: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7"></circle><polyline points="8.21,13.89 7,23 12,20 17,23 15.79,13.88"></polyline>
    </svg>
  ),
  AlertCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  ),
  Play: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5,3 19,12 5,21 5,3"></polygon>
    </svg>
  ),
  RefreshCw: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23,4 23,10 17,10"></polyline><polyline points="1,20 1,14 7,14"></polyline>
      <path d="M3.51,9a9,9,0,0,1,14.85-3.36L23,10M1,14l4.64,4.36A9,9,0,0,0,20.49,15"></path>
    </svg>
  ),
};

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  headerGradient: { background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0284c7 100%)', color: 'white', padding: '20px 16px', paddingTop: '24px' },
  card: { background: 'white', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)', padding: '20px', marginBottom: '12px' },
  buttonPrimary: { background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)', color: 'white', border: 'none', borderRadius: '12px', padding: '14px 24px', fontWeight: '600', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  buttonSecondary: { background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '12px', padding: '14px 24px', fontWeight: '600', fontSize: '15px', cursor: 'pointer' },
  badge: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  sectionHeader: { fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', marginTop: '24px' },
};

const pulseAnimation = `
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const TrainingView = ({ onBack, employeeData, dashboardData }) => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleContent, setModuleContent] = useState(null);
  const [currentView, setCurrentView] = useState('list');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResults, setQuizResults] = useState(null);

  useEffect(() => { loadModules(); }, []);

  const loadModules = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      const empId = employeeData?.id || dashboardData?.employeeId;
      if (empId) params.employee_id = empId;
      const response = await portalApi.get('/documents/training/modules', { params });
      if (response.data.success) {
        setModules(response.data.modules || []);
      } else {
        setModules(response.data.modules || response.data || []);
      }
    } catch (err) {
      console.error('Failed to load training modules:', err);
      setError('Unable to load training modules. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadModuleContent = async (moduleId) => {
    setLoading(true);
    setError(null);
    const empId = employeeData?.id || dashboardData?.employeeId;
    if (!empId) {
      setError('Unable to identify employee. Please log out and back in.');
      setLoading(false);
      return;
    }
    try {
      const response = await portalApi.post(`/documents/training/start/${moduleId}`, { employee_id: empId });
      if (response.data.success) {
        setModuleContent(response.data);
        setCurrentView('content');
      } else {
        setError(response.data.error || 'Unable to load module content.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load module content.');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setQuizResults(null);
    setLoading(true);
    try {
      const response = await portalApi.get(`/documents/training/question/${moduleContent.assessment_id}`);
      if (response.data.success) {
        if (response.data.completed) { await completeQuiz(); return; }
        setModuleContent(prev => ({ ...prev, currentQuestion: response.data.question, progress: response.data.progress, questions: [response.data.question] }));
        setCurrentView('quiz');
      } else {
        setError(response.data.error || 'Unable to load questions.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load questions.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answerId) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  };

  const submitAnswer = async (questionId, answerValue) => {
    const response = await portalApi.post('/documents/training/answer', {
      assessment_id: moduleContent.assessment_id,
      question_id: questionId,
      answer: answerValue,
    });
    return response.data;
  };

  const fetchNextQuestion = async () => {
    const response = await portalApi.get(`/documents/training/question/${moduleContent.assessment_id}`);
    if (response.data.success) {
      if (response.data.completed) { await completeQuiz(); return null; }
      setModuleContent(prev => ({ ...prev, currentQuestion: response.data.question, progress: response.data.progress }));
      return response.data.question;
    }
    return null;
  };

  const completeQuiz = async () => {
    setQuizSubmitting(true);
    try {
      const response = await portalApi.post(`/documents/training/complete/${moduleContent.assessment_id}`, { employee_id: employeeData?.id });
      if (response.data.success) {
        setQuizResults({ score: response.data.score, passed: response.data.passed, total_questions: response.data.total_points, correct_answers: response.data.earned_points, certificate_number: response.data.certificate_number, passing_score: response.data.passing_score });
        setCurrentView('results');
        loadModules();
      } else {
        setError(response.data.error || 'Failed to complete assessment.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete assessment.');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const getStatusBadge = (module) => {
    if (module.completed || module.status === 'COMPLETED') return { text: 'Completed', bg: '#dcfce7', color: '#166534' };
    if (module.in_progress || module.status === 'IN_PROGRESS') return { text: 'In Progress', bg: '#fef3c7', color: '#92400e' };
    if (module.status === 'FAILED') return { text: 'Retry Available', bg: '#fee2e2', color: '#991b1b' };
    return { text: 'Not Started', bg: '#f1f5f9', color: '#475569' };
  };

  const ModuleListView = () => (
    <div style={styles.container}>
      <style>{pulseAnimation}</style>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.ArrowLeft /></button>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>My Training</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Complete your assigned modules</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          {[['Assigned', modules.length], ['Completed', modules.filter(m => m.completed || m.status === 'COMPLETED').length]].map(([label, val]) => (
            <div key={label} style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '16px', flex: 1 }}>
              <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '24px', fontWeight: '700' }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : error ? (
          <div style={{ ...styles.card, background: '#fef2f2', border: '1px solid #fecaca', textAlign: 'center' }}>
            <Icons.AlertCircle />
            <p style={{ color: '#991b1b', margin: '12px 0' }}>{error}</p>
            <button onClick={loadModules} style={styles.buttonSecondary}>Try Again</button>
          </div>
        ) : modules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎓</div>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>No Training Assigned</div>
            <div style={{ fontSize: '14px' }}>Check back later for new modules</div>
          </div>
        ) : (
          <>
            {modules.filter(m => !m.completed && m.status !== 'COMPLETED').length > 0 && (
              <>
                <div style={styles.sectionHeader}>To Complete</div>
                {modules.filter(m => !m.completed && m.status !== 'COMPLETED').map((module) => {
                  const status = getStatusBadge(module);
                  return (
                    <div key={module.id || module.module_id} onClick={() => { setSelectedModule(module); loadModuleContent(module.id || module.module_id); }} style={{ ...styles.card, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>{module.title || module.module_title}</div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>{module.description || module.module_description || 'Complete this training module'}</div>
                        </div>
                        <span style={{ ...styles.badge, background: status.bg, color: status.color }}>{status.text}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#64748b' }}>
                        {module.time_estimate && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Icons.Clock /> {module.time_estimate}</span>}
                        {module.question_count && <span>{module.question_count} questions</span>}
                        {module.passing_score && <span>Pass: {module.passing_score}%</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '12px', color: '#0d9488', fontWeight: '600', fontSize: '14px' }}>
                        Start Training <Icons.ChevronRight />
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {modules.filter(m => m.completed || m.status === 'COMPLETED').length > 0 && (
              <>
                <div style={styles.sectionHeader}>Completed</div>
                {modules.filter(m => m.completed || m.status === 'COMPLETED').map((module) => (
                  <div key={module.id || module.module_id} onClick={() => { setSelectedModule(module); loadModuleContent(module.id || module.module_id); }} style={{ ...styles.card, cursor: 'pointer', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px' }}>{module.title || module.module_title}</div>
                        <div style={{ fontSize: '13px', color: '#15803d' }}>
                          {module.score !== undefined && `Score: ${module.score}%`}
                          {module.completed_at && ` • Completed ${moment(module.completed_at).format('D MMM YYYY')}`}
                        </div>
                      </div>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.Check /></div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  const ModuleContentView = () => (
    <div style={styles.container}>
      <style>{pulseAnimation}</style>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => { setCurrentView('list'); setSelectedModule(null); setModuleContent(null); }} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.ArrowLeft /></button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>{selectedModule?.title || selectedModule?.module_title}</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>{moduleContent?.time_estimate || selectedModule?.time_estimate || 'Training Module'}</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '16px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            <div style={{ ...styles.card, background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)', border: '1px solid #99f6e4' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.Book /></div>
                <div>
                  <div style={{ fontWeight: '600', color: '#134e4a' }}>{moduleContent?.question_count || selectedModule?.question_count || '?'} Questions</div>
                  <div style={{ fontSize: '13px', color: '#0f766e' }}>Pass mark: {moduleContent?.passing_score || selectedModule?.passing_score || 70}%</div>
                </div>
              </div>
            </div>
            <div style={styles.card}>
              <h3 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>About This Module</h3>
              <div style={{ color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {moduleContent?.content || moduleContent?.description || selectedModule?.description || 'This training module will test your knowledge and understanding. Read through the material carefully before starting the quiz.'}
              </div>
            </div>
            {(moduleContent?.objectives || moduleContent?.learning_objectives) && (
              <div style={styles.card}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Learning Objectives</h3>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569' }}>
                  {(moduleContent.objectives || moduleContent.learning_objectives).map((obj, i) => <li key={i} style={{ marginBottom: '8px' }}>{obj}</li>)}
                </ul>
              </div>
            )}
            <button onClick={startQuiz} style={{ ...styles.buttonPrimary, width: '100%', marginTop: '16px', padding: '18px', fontSize: '16px' }}><Icons.Play /> Start Quiz</button>
            {selectedModule?.score !== undefined && (
              <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '14px', color: '#64748b' }}>
                Previous attempt: {selectedModule.score}%{selectedModule.score >= (moduleContent?.passing_score || 70) ? ' ✓ Passed' : ' - Retry to improve'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const QuizView = () => {
    const [submittingAnswer, setSubmittingAnswer] = useState(false);
    const [answerFeedback, setAnswerFeedback] = useState(null);
    const currentQuestion = moduleContent?.currentQuestion;
    const progress = moduleContent?.progress || { current: 1, total: 1, answered: 0 };
    const currentAnswer = answers[currentQuestion?.id];

    if (!currentQuestion) {
      return (
        <div style={styles.container}>
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '48px auto' }} />
            <p>Loading question...</p>
          </div>
        </div>
      );
    }

    const handleSubmitAndNext = async () => {
      if (!currentAnswer) return;
      setSubmittingAnswer(true);
      setAnswerFeedback(null);
      try {
        const result = await submitAnswer(currentQuestion.id, currentAnswer);
        setAnswerFeedback({ isCorrect: result.is_correct, explanation: result.explanation });
        setTimeout(async () => {
          setAnswerFeedback(null);
          await fetchNextQuestion();
          setSubmittingAnswer(false);
        }, 1500);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to submit answer.');
        setSubmittingAnswer(false);
      }
    };

    const options = currentQuestion.options || [];

    return (
      <div style={styles.container}>
        <style>{pulseAnimation}</style>
        <div style={{ background: 'white', padding: '16px', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <button onClick={() => { if (window.confirm('Are you sure you want to exit? Your progress will be saved.')) setCurrentView('content'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <Icons.ArrowLeft /> Exit Quiz
            </button>
            <span style={{ fontWeight: '600', color: '#1e293b' }}>Question {progress.current} of {progress.total}</span>
          </div>
          <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)', height: '100%', width: `${(progress.answered / progress.total) * 100}%`, transition: 'width 0.3s ease' }} />
          </div>
        </div>
        <div style={{ padding: '16px' }}>
          {answerFeedback && (
            <div style={{ ...styles.card, background: answerFeedback.isCorrect ? '#dcfce7' : '#fef3c7', border: answerFeedback.isCorrect ? '1px solid #86efac' : '1px solid #fcd34d', marginBottom: '16px' }}>
              <div style={{ fontWeight: '600', color: answerFeedback.isCorrect ? '#166534' : '#92400e', marginBottom: '4px' }}>{answerFeedback.isCorrect ? '✓ Correct!' : '✗ Not quite right'}</div>
              {answerFeedback.explanation && <div style={{ fontSize: '14px', color: answerFeedback.isCorrect ? '#15803d' : '#a16207' }}>{answerFeedback.explanation}</div>}
            </div>
          )}
          <div style={{ ...styles.card, marginBottom: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0, lineHeight: '1.5' }}>{currentQuestion.question_text || currentQuestion.text || currentQuestion.question}</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {options.map((option, index) => {
              const optionValue = option.value || option.id || String.fromCharCode(65 + index);
              const optionText = option.text || option.answer_text || option;
              const isSelected = currentAnswer === optionValue;
              return (
                <button key={index} onClick={() => handleAnswerSelect(currentQuestion.id, optionValue)} disabled={submittingAnswer} style={{ ...styles.card, border: isSelected ? '2px solid #0d9488' : '2px solid #e2e8f0', background: isSelected ? '#f0fdfa' : 'white', cursor: submittingAnswer ? 'not-allowed' : 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '16px', marginBottom: 0, opacity: submittingAnswer ? 0.7 : 1 }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: isSelected ? '2px solid #0d9488' : '2px solid #cbd5e1', background: isSelected ? '#0d9488' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isSelected ? 'white' : '#64748b', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>{String.fromCharCode(65 + index)}</div>
                  <span style={{ color: isSelected ? '#0d9488' : '#334155', fontWeight: isSelected ? '600' : '400', fontSize: '15px' }}>{optionText}</span>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: '24px' }}>
            <button onClick={handleSubmitAndNext} disabled={!currentAnswer || submittingAnswer} style={{ ...styles.buttonPrimary, width: '100%', opacity: !currentAnswer || submittingAnswer ? 0.5 : 1 }}>
              {submittingAnswer ? 'Submitting...' : 'Submit Answer'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ResultsView = () => {
    const [showCertificate, setShowCertificate] = useState(false);
    const [certificateId, setCertificateId] = useState(null);
    const passed = quizResults?.passed || (quizResults?.score >= (moduleContent?.passing_score || 70));
    const score = quizResults?.score || 0;

    useEffect(() => {
      if (passed && quizResults?.certificate_number && !certificateId) {
        (async () => {
          try {
            const empId = employeeData?.id || dashboardData?.employeeId;
            const response = await portalApi.get(`/documents/training/certificates/employee/${empId}`);
            if (response.data.success && response.data.certificates.length > 0) {
              setCertificateId(response.data.certificates[0].id);
            }
          } catch (err) {
            console.error('Failed to fetch certificate:', err);
          }
        })();
      }
    }, [passed, quizResults?.certificate_number]);

    return (
      <div style={styles.container}>
        <style>{pulseAnimation}</style>
        <div style={{ ...styles.headerGradient, background: passed ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', textAlign: 'center', padding: '48px 16px' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ fontSize: '40px' }}>{passed ? '🎉' : '📚'}</span>
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 8px 0' }}>{passed ? 'Congratulations!' : 'Keep Learning!'}</h1>
          <p style={{ fontSize: '16px', opacity: 0.9, margin: 0 }}>{passed ? 'You have successfully completed this module' : 'You need a bit more practice'}</p>
        </div>
        <div style={{ padding: '16px', marginTop: '-32px' }}>
          <div style={{ ...styles.card, textAlign: 'center', paddingTop: '32px', paddingBottom: '32px' }}>
            <div style={{ fontSize: '64px', fontWeight: '700', color: passed ? '#10b981' : '#f59e0b', marginBottom: '8px' }}>{score}%</div>
            <div style={{ color: '#64748b', marginBottom: '16px' }}>Pass mark: {moduleContent?.passing_score || 70}%</div>
            {quizResults?.correct_answers !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <div><div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>{quizResults.correct_answers}</div><div style={{ fontSize: '13px', color: '#64748b' }}>Correct</div></div>
                <div><div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444' }}>{quizResults.total_questions - quizResults.correct_answers}</div><div style={{ fontSize: '13px', color: '#64748b' }}>Incorrect</div></div>
              </div>
            )}
          </div>
          {passed && quizResults?.certificate_number && (
            <div style={{ ...styles.card, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #fbbf24', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '28px' }}>🏆</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: '#78350f' }}>Certificate Earned!</h3>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#92400e' }}>Certificate: {quizResults.certificate_number}</p>
                  <button onClick={() => setShowCertificate(true)} disabled={!certificateId} style={{ ...styles.buttonPrimary, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', padding: '10px 20px', fontSize: '14px', opacity: certificateId ? 1 : 0.5 }}>
                    <Icons.Award /> View Certificate
                  </button>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            {!passed && (
              <button onClick={() => { setAnswers({}); setCurrentQuestionIndex(0); setQuizResults(null); loadModuleContent(selectedModule.id || selectedModule.module_id); }} style={{ ...styles.buttonPrimary, width: '100%' }}>
                <Icons.RefreshCw /> Try Again
              </button>
            )}
            <button onClick={() => { setCurrentView('list'); setSelectedModule(null); setModuleContent(null); setQuizResults(null); }} style={{ ...styles.buttonSecondary, width: '100%' }}>
              Back to Training List
            </button>
          </div>
        </div>
        {showCertificate && certificateId && <TrainingCertificate certificateId={certificateId} onClose={() => setShowCertificate(false)} />}
      </div>
    );
  };

  switch (currentView) {
    case 'content': return <ModuleContentView />;
    case 'quiz': return <QuizView />;
    case 'results': return <ResultsView />;
    default: return <ModuleListView />;
  }
};

export default TrainingView;